import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type {
  GetAllCountriesResult,
  GetLineItemConfigurationsResult,
  GetTaxCodesByCountryResult,
  ILineItemTaxPhoenixApi,
} from './api';
import { LineItemTaxPhoenixApi } from './api';
import type {
  CountriesTaxCodesControllerResultTypes,
  CreateLineItemConfigurationDto,
  LineItemConfigurationControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import type { CountryWithTranslatedLabel } from '../line-item-tax';
import {
  CREATE_TAX_LINE_CONFIGURATION,
  DELETE_TAX_LINE_CONFIGURATION,
  GET_ALL_COUNTRIES,
  GET_TAX_CODES_BY_COUNTRY,
  GET_TAX_LINE_CONFIGURATIONS,
} from '../line-item-tax';

const ApiContext = createContext<ILineItemTaxPhoenixApi | undefined>(undefined);
const {
  useApi: useLineItemTaxPhoenixApi,
  ApiProvider: LineItemTaxPhoenixApiProvider,
} = makeApiProvider({
  name: 'LineItemTaxPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new LineItemTaxPhoenixApi(tenantId, auth, apiUrl),
});

export {
  useLineItemTaxPhoenixApi,
  LineItemTaxPhoenixApiProvider,
  ApiContext as LineItemTaxPhoenixApiContext,
};
export type { ILineItemTaxPhoenixApi } from './api';

export const useGetTaxCodesByCountryPhoenix = (
  countryId: string | undefined
): UseQueryResult<CountriesTaxCodesControllerResultTypes['getTaxCodes']> => {
  const select = useCallback(
    (
      data: GetTaxCodesByCountryResult
    ): CountriesTaxCodesControllerResultTypes['getTaxCodes'] => {
      return data.data.map((row) => ({
        id: row.id,
        countryId: row.country.id,
        countryIsoCode: row.country.iso_code,
        name: row.tax_name,
        countryIsoCode3: row.country.iso_code_3,
        countryName: row.country.name,
        taxRatio: parseFloat(row.tax_amount) * 100,
        abbreviation: row.tax_abbreviation,
        displayTaxRatioExplanation: row.display_explanation,
        taxRatioExplanation: row.explanation_long,
        paragraph: row.paragraph,
      }));
    },
    []
  );

  return useApiQuery(
    useLineItemTaxPhoenixApi,
    GET_TAX_CODES_BY_COUNTRY(countryId),
    (api) => {
      if (!countryId) {
        throw new Error('countryId is required');
      }

      return api.getTaxCodesByCountry({
        params: {
          countryId,
        },
      });
    },
    { enabled: !!countryId, select }
  );
};

export const useGetAllCountriesPhoenix = (
  options?: UseQueryOptions<
    GetAllCountriesResult,
    unknown,
    CountryWithTranslatedLabel[]
  >
): UseQueryResult<CountryWithTranslatedLabel[]> => {
  const { t } = useTranslation();

  const translateAndSortCountries = useCallback(
    (
      countries: GetAllCountriesResult
    ): CountryWithTranslatedLabel[] | undefined => {
      const options = countries.data?.map<CountryWithTranslatedLabel>(
        (country) => {
          return {
            id: country.id,
            isoCode: country.iso_code,
            name: country.name,
            fullName: country.full_name,
            isoCode3: country.iso_code_3,
            numericCode: country.numeric_code,
            value: country.id,
            label: t(`country.${country.iso_code}`),
          };
        }
      );

      options?.sort((a, b) => a.label.localeCompare(b.label));
      return options;
    },
    [t]
  );

  return useApiQuery<
    LineItemTaxPhoenixApi,
    CountryWithTranslatedLabel[],
    GetAllCountriesResult
  >(
    useLineItemTaxPhoenixApi,
    GET_ALL_COUNTRIES(),
    (api) => {
      return api.getAllCountries();
    },
    {
      ...options,
      select: (data) => translateAndSortCountries(data) ?? [],
    }
  );
};

export const useGetTaxLineConfigurationsPhoenix = (
  globalServiceProviderId: string | undefined
): UseQueryResult<
  LineItemConfigurationControllerResultTypes['getLineItemConfigurations']
> => {
  const select = useCallback(
    (
      data: GetLineItemConfigurationsResult
    ): LineItemConfigurationControllerResultTypes['getLineItemConfigurations'] => {
      return data.data.map((row) => ({
        id: row.id,
        name: row.name,
        globalServiceProviderId: row.global_service_provider_id,
        amount: row.amount,
        taxCodes: row.taxes.map((tax) => ({
          id: tax.id,
          countryId: tax.country_id,
          displayExplanation: tax.display_explanation,
          explanationLong: tax.explanation_long,
          explanationShort: tax.explanation_short,
          taxAbbreviation: tax.tax_abbreviation,
          taxAmount: parseFloat(tax.tax_amount),
          taxName: tax.tax_name,
        })),
      }));
    },
    []
  );

  return useApiQuery(
    useLineItemTaxPhoenixApi,
    GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId),
    (api) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.getLineItemConfigurations();
    },
    {
      enabled: !!globalServiceProviderId,
      select,
    }
  );
};

export const useCreateLineItemConfigurationPhoenix = (
  globalServiceProviderId: string | undefined
): UseMutationResult<void, unknown, CreateLineItemConfigurationDto> => {
  const api = useLineItemTaxPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, CreateLineItemConfigurationDto>(
    CREATE_TAX_LINE_CONFIGURATION(globalServiceProviderId),
    async (data) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      await api.createLineItemConfiguration({
        body: data,
      });
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(
          GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId)
        );
      },
    }
  );
};

export const useDeleteLineItemConfigurationPhoenix = (
  globalServiceProviderId: string | undefined
): UseMutationResult<
  { status: 'OK' },
  unknown,
  {
    id: string;
  }
> => {
  const api = useLineItemTaxPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    { status: 'OK' },
    unknown,
    {
      id: string;
    }
  >(
    DELETE_TAX_LINE_CONFIGURATION(globalServiceProviderId),
    (data) => {
      return api.deleteLineItemConfiguration({
        params: {
          id: data.id,
        },
      });
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(
          GET_TAX_LINE_CONFIGURATIONS(globalServiceProviderId)
        );
      },
    }
  );
};
